import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, MapPin, CheckCircle2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    organization: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success('Thank you! We will be in touch within 24 hours.');
        setFormData({
          name: '',
          title: '',
          organization: '',
          email: '',
          phone: '',
          message: '',
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const reasons = [
    'Expert guidance from healthcare reimbursement specialists',
    'Proven track record with hospitals and health systems',
    'Strategic, outcomes-focused approach',
    'Dedicated partnership from assessment to implementation',
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="hero-texture bg-[#0F172A] relative">
        <div className="container-custom relative z-10 py-24 sm:py-32">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
              Contact Us
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-white font-['Outfit']">
              Let's Start a Conversation
            </h1>
            <p className="mt-6 text-xl text-gray-300 leading-relaxed">
              Whether you're facing reimbursement challenges, compliance concerns, or looking to optimize your revenue cycle—we're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="contact-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div className="bg-white border border-[#E2E8F0] p-8 sm:p-12" data-testid="contact-form-container">
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="success-message">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#0D9488] flex items-center justify-center mb-6">
                    <CheckCircle2 className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-semibold text-[#0F172A] font-['Outfit'] mb-4">
                    Thank You!
                  </h2>
                  <p className="text-[#64748B] mb-8">
                    Your inquiry has been received. A member of our team will contact you within 24 hours to discuss your needs.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white"
                    data-testid="submit-another-btn"
                  >
                    Submit Another Inquiry
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold text-[#0F172A] font-['Outfit'] mb-2">
                    Schedule a Consultation
                  </h2>
                  <p className="text-[#64748B] mb-8">
                    Fill out the form below and we'll be in touch within 24 hours.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-[#0F172A] font-medium mb-2 block">
                          Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="John Smith"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="title" className="text-[#0F172A] font-medium mb-2 block">
                          Title *
                        </Label>
                        <Input
                          id="title"
                          name="title"
                          type="text"
                          required
                          value={formData.title}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="VP of Revenue Cycle"
                          data-testid="input-title"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="organization" className="text-[#0F172A] font-medium mb-2 block">
                        Organization *
                      </Label>
                      <Input
                        id="organization"
                        name="organization"
                        type="text"
                        required
                        value={formData.organization}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Memorial Health System"
                        data-testid="input-organization"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email" className="text-[#0F172A] font-medium mb-2 block">
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="john.smith@hospital.org"
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-[#0F172A] font-medium mb-2 block">
                          Phone
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="(555) 123-4567"
                          data-testid="input-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-[#0F172A] font-medium mb-2 block">
                        How Can We Help? *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        className="form-input min-h-[150px]"
                        placeholder="Tell us about your reimbursement challenges or areas of interest..."
                        data-testid="input-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#0D9488] hover:bg-[#0f766e] text-white rounded-md py-6 text-lg group"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner mr-2"></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Inquiry
                          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div>
              {/* Why Work With Us */}
              <div className="mb-12" data-testid="why-work-with-us">
                <h3 className="text-xl font-semibold text-[#0F172A] font-['Outfit'] mb-6">
                  Why Work With Us
                </h3>
                <ul className="space-y-4">
                  {reasons.map((reason, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="h-6 w-6 text-[#0D9488] flex-shrink-0 mt-0.5" />
                      <span className="text-[#64748B]">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact Info */}
              <div className="bg-[#0F172A] p-8 text-white" data-testid="contact-info">
                <h3 className="text-xl font-semibold font-['Outfit'] mb-6">
                  Get in Touch
                </h3>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#1e293b] flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-[#0D9488]" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Email</p>
                      <a
                        href="mailto:deirdre.lewis@symplicity.org"
                        className="hover:text-[#0D9488] transition-colors"
                        data-testid="contact-email"
                      >
                        deirdre.lewis@symplicity.org
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#1e293b] flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-[#0D9488]" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Service Area</p>
                      <p>Serving Healthcare Organizations Nationwide</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Response Time */}
              <div className="mt-8 bg-white border border-[#E2E8F0] p-6" data-testid="response-time">
                <p className="text-[#0F172A] font-medium">
                  We respond to all inquiries within 24 hours.
                </p>
                <p className="text-[#64748B] text-sm mt-2">
                  For urgent matters, please call us directly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
